'use client';
// NODE LIBRARIES
import { useSearchParams } from 'next/navigation';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_SEVERITY, LoadingProgress } from 'swp-react';
// JS HELPER UTILS
import { fetchCall } from '../utils/fetchUtils';
import netreq from '@/utils/netreq';
import { getBrowserNameFromUserAgent } from '@/utils/miscUtils';
// REDUX SLICES
import {
	selectAccessCode,
	selectUserId,
	setAccessCode,
	setRetailerId,
	setUserId,
	setDocMode,
	selectRetailerId,
	setInputSource,
	setKnumber,
	setHostName,
	setLocalDomain,
	selectUserDeviceData,
	setUserDeviceData,
} from '../slices/commonSlice';
import { selectUploadInProgress } from '@/slices/uploadSlice';
// REACT COMPONENTS
import CloudTransfer from '../components/CloudTransfer/CloudTransfer';
import HelpPage from '../components/HelpPage';
import UploadProgress from '@/components/UploadProgress/UploadProgress';
import { ConfigContext } from '../components/UserAgentContext';
// LOGGING
import {
	logToAi,
	updateKnumber,
	updateLoggerId,
	updateRetailerId,
} from '@/utils/logging';
import { aR, bcT, bcB, bT, yT } from '@/utils/logStyling';
// JS DOC TYPES
import '../utils/typeDefs';

const valid = (obj) => obj !== null && obj !== undefined;

export default function Index(props) {
	// Hooks
	const params = useSearchParams();
	const dispatch = useDispatch();
	// Selectors
	const accessCode = useSelector(selectAccessCode);
	const userId = useSelector(selectUserId);
	const retailerId = useSelector(selectRetailerId);
	const uploadInProgress = useSelector(selectUploadInProgress);
	const userDeviceData = useSelector(selectUserDeviceData);
	const config = useContext(ConfigContext);
	// State
	const [isLoading, setIsLoading] = useState(true);
	const [showCloudTransfer, setShowCloudTransfer] = useState(false);

	//#region Log User Device Data
	/**
	 * Collects details about the user's device and logs it to application
	 * insights so we can see what the average performance of our users is and
	 * improve our application accordingly.
	 */
	// useEffect(() => {
	// 	const logUserDeviceData = async () => {
	// 		try {
	// 			if (Object.keys(userDeviceData).length > 0) {
	// 				// The device data must have already been logged. Bail!
	// 				return;
	// 			}
	// 			const nav = window?.navigator ?? navigator;
	// 			// https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/getHighEntropyValues
	// 			const userAgentData =
	// 				(await nav?.userAgentData?.getHighEntropyValues?.([
	// 					'architecture',
	// 					'bitness',
	// 					'formFactors',
	// 					'model',
	// 					'platformVersion',
	// 					'wow64',
	// 				])) ??
	// 				nav?.userAgentData ??
	// 				null;
	// 			let dataToLog = {
	// 				userAgentData: userAgentData,
	// 				...nav,
	// 				userAgent: getBrowserNameFromUserAgent(nav?.userAgent),
	// 				platform: nav?.platform,
	// 				vendor: nav?.vendor,
	// 				network: nav?.connection ?? null,
	// 				deviceMemory: nav?.deviceMemory ?? null,
	// 				devicePosture: nav?.devicePosture ?? null,
	// 				defaultLanguage: nav?.language,
	// 				knownLanguages: nav?.languages ?? [],
	// 				webdriver: nav?.webdriver ?? false,
	// 			};
	// 			dispatch(setUserDeviceData(dataToLog));
	// 			logToAi(
	// 				{
	// 					message:
	// 						'User data to be used for the improvement of the' +
	// 						' application has been successfully obtained.',
	// 					id: 'LOG_USER_DATA',
	// 					location: 'index.js:useEffect',
	// 					data: {
	// 						...dataToLog,
	// 					},
	// 				},
	// 				DEFAULT_SEVERITY.INFORMATION
	// 			);
	// 		} catch (error) {
	// 			dispatch(setUserDeviceData({ error: error }));
	// 			logToAi(
	// 				{
	// 					message:
	// 						'Unable to access the navigator object and obtain' +
	// 						'data about the device and browser.',
	// 					id: 'LOG_USER_DATA_FAILURE',
	// 					location: 'index.js:userData',
	// 					data: {
	// 						error: error,
	// 					},
	// 				},
	// 				DEFAULT_SEVERITY.WARNING
	// 			);
	// 		}
	// 	};
	// 	logUserDeviceData();
	// }, [userDeviceData]);
	//#endregion

	useEffect(() => {
		const jwtToken = params?.get('jwt');
		const enableCloudTransfer = config?.enableCloudTransfer === 'true';
		const cloudInfoAvailable = userId && retailerId && true;
		const allowedRetailers = [
			'dm',
			'cvs',
			'other',
			'KinneyDrugs',
			'carrefour-FRA',
			'KMMP_Demo_Retailer',
		];
		if (jwtToken && enableCloudTransfer) {
			fetchCall('/api/decrypt', 'GET', null, { token: jwtToken })
				.then(async (/** @type {JwtDataType} */ response) => {
					let validRetailer = false;
					if (response.ac) {
						dispatch(setAccessCode(response.ac));
					}
					if (response.kn) {
						dispatch(setKnumber(response.kn));
						updateKnumber(response.kn);
					}
					if (response.uid) {
						dispatch(setUserId(response.uid));
						updateLoggerId(response.uid);
					}
					if (response?.rid) {
						validRetailer = allowedRetailers.includes(response.rid);
						dispatch(setRetailerId(response.rid));
						updateRetailerId(response.rid);
					}
					if (response.inp) {
						const inputSource = response.inp
							?.split(',')
							?.map((source) => source?.trim());
						dispatch(setInputSource(inputSource));
					}
					if (valid(response.doc)) {
						dispatch(setDocMode(response.doc));
					}
					if (response.host) {
						dispatch(setHostName(response.host));
					}
					if (response.ld) {
						try {
							const isAlive = await netreq(
								`${response.ld}/spelite/isalive`,
								'GET'
							);
							dispatch(setLocalDomain(response.ld));
						} catch (err) {
							logToAi(
								{
									message: `local domain is not accessible`,
									id: 'LOCAL_DOMAIN_FAILURE',
									location: 'index.js:useEffect',
									data: {
										error: err,
									},
								},
								'error'
							);
							alert(`something went wrong ${err}`);
						}
					}
					if (!response.uid || !validRetailer) {
						throw new Error(
							`One of the required parameters is missing ${JSON.stringify(
								response
							)}`
						);
					}
					setShowCloudTransfer(true);
					setIsLoading(false);
				})
				.catch((error) => {
					logToAi(
						{
							message: `Error attempting to decrypt token!`,
							id: 'API_DECRYPT_ERROR',
							location: 'index.js:useEffect',
							data: {
								error: error,
							},
						},
						'error'
					);
					setShowCloudTransfer(false);
					setIsLoading(false);
				});
		} else if (cloudInfoAvailable && enableCloudTransfer) {
			setShowCloudTransfer(true);
			setIsLoading(false);
		} else {
			setIsLoading(false);
		}
	}, [params]);

	if (isLoading) {
		return (
			<div className="loading-spinner-container">
				<LoadingProgress />
			</div>
		);
	}

	if (showCloudTransfer) {
		if (uploadInProgress) {
			return <UploadProgress />;
		}
		return <CloudTransfer />;
	}

	return <HelpPage />;
}
